import Decimal from 'decimal.js'
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import { Header, Segment } from 'semantic-ui-react'
import { serverUrl } from '../../../../utilites/config'
import { reconnectingSocket } from '../../../../utilites/socket'

const CAMERA_IMAGE_URL = 'https://episoft.pl/~ep/img001.jpg'
const MILLISECONDS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const WEATHER_REFRESH_MINUTES = 10
const CAMERA_REFRESH_MS = MILLISECONDS_PER_SECOND
const CAMERA_STALE_MS = 15_000
const CAMERA_STALENESS_CHECK_MS = MILLISECONDS_PER_SECOND
const TELESCOPE_POSITION_URL = `${serverUrl}/telescope/position`
const TELESCOPE_POSITION_TOPIC = '/topic/telescope-position'
const SUN_POSITION_REFRESH_MS = 10_000
const OBSERVATORY_LATITUDE_DEGREES = 51.110548
const OBSERVATORY_LONGITUDE_DEGREES = 17.025558
const HALF_CIRCLE_DEGREES = new Decimal(180)
const FULL_CIRCLE_DEGREES = new Decimal(360)
const WIND_DIRECTION_SECTOR_DEGREES = 45
const DEGREE_DECIMAL_PLACES = 2
const POSITIVE_DEGREES_THRESHOLD = 0
const INITIAL_REQUEST_ID = 0
const HTTP_NOT_FOUND = 404

const WEATHER_REFRESH_MS = WEATHER_REFRESH_MINUTES * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND
const UI_BORDER = '1px solid rgba(34, 36, 38, 0.12)'
const UI_BORDER_RADIUS_PX = 6
const PAGE_GAP = '1.5rem'
const CARD_GAP = '0.8rem'
const METRIC_PADDING = '0.9rem'
const METRIC_LABEL_FONT_SIZE = '0.9rem'
const METRIC_VALUE_FONT_SIZE = '1.35rem'
const METRIC_LABEL_MARGIN_BOTTOM = '0.25rem'
const STATUS_MARGIN_TOP = '0.7rem'
const METRIC_VALUE_FONT_WEIGHT = 700
const CAMERA_MAX_HEIGHT = '70vh'
const WEATHER_CARD_MIN_WIDTH = '150px'
const TELESCOPE_LABEL_MIN_WIDTH = '120px'
const TELESCOPE_LABEL_COLUMN_FRACTION = '0.8fr'
const TELESCOPE_VALUE_COLUMN_COUNT = 2
const TELESCOPE_VALUE_MIN_WIDTH = '150px'
const LOADING_CAMERA_PADDING = '3rem 1rem'

const JULIAN_UNIX_EPOCH = 2_440_587.5
const JULIAN_J2000_EPOCH = 2_451_545
const MILLISECONDS_PER_DAY = 86_400_000
const DAYS_PER_JULIAN_CENTURY = 36_525
const SIDEREAL_TIME_CUBIC_DIVISOR = 38_710_000
const ANGULAR_DIFF_OFFSET_DEGREES = FULL_CIRCLE_DEGREES.plus(HALF_CIRCLE_DEGREES)
const SECOND_HARMONIC_MULTIPLIER = 2
const SQUARE_EXPONENT = 2
const CUBE_EXPONENT = 3
const SUN_MEAN_LONGITUDE_BASE_DEGREES = new Decimal(280.46)
const SUN_MEAN_LONGITUDE_RATE_DEGREES = new Decimal(0.9856474)
const SUN_MEAN_ANOMALY_BASE_DEGREES = new Decimal(357.528)
const SUN_MEAN_ANOMALY_RATE_DEGREES = new Decimal(0.9856003)
const SUN_ECLIPTIC_LONGITUDE_FIRST_COEFFICIENT = new Decimal(1.915)
const SUN_ECLIPTIC_LONGITUDE_SECOND_COEFFICIENT = new Decimal(0.02)
const EARTH_OBLIQUITY_BASE_DEGREES = new Decimal(23.439)
const EARTH_OBLIQUITY_RATE_DEGREES = new Decimal(0.0000004)
const GREENWICH_MEAN_SIDEREAL_TIME_BASE_DEGREES = new Decimal(280.46061837)
const GREENWICH_MEAN_SIDEREAL_TIME_RATE_DEGREES = new Decimal(360.98564736629)
const GREENWICH_MEAN_SIDEREAL_TIME_QUADRATIC_COEFFICIENT = new Decimal(0.000387933)

const WMO_WEATHER_CODES = {
  CLEAR_SKY: 0,
  MAINLY_CLEAR: 1,
  PARTLY_CLOUDY: 2,
  OVERCAST: 3,
  FOG: 45,
  DEPOSITING_RIME_FOG: 48,
  LIGHT_DRIZZLE: 51,
  MODERATE_DRIZZLE: 53,
  DENSE_DRIZZLE: 55,
  SLIGHT_RAIN: 61,
  MODERATE_RAIN: 63,
  HEAVY_RAIN: 65,
  SLIGHT_SNOW: 71,
  MODERATE_SNOW: 73,
  HEAVY_SNOW: 75,
  SLIGHT_RAIN_SHOWERS: 80,
  MODERATE_RAIN_SHOWERS: 81,
  VIOLENT_RAIN_SHOWERS: 82,
  THUNDERSTORM: 95,
  THUNDERSTORM_SLIGHT_HAIL: 96,
  THUNDERSTORM_HEAVY_HAIL: 99,
} as const
const WEATHER_URL = (() => {
  const url = new URL('https://api.open-meteo.com/v1/forecast')
  url.searchParams.set('latitude', OBSERVATORY_LATITUDE_DEGREES.toString())
  url.searchParams.set('longitude', OBSERVATORY_LONGITUDE_DEGREES.toString())
  url.searchParams.set('current', 'temperature_2m,cloud_cover,wind_speed_10m,wind_direction_10m,weather_code')
  url.searchParams.set('timezone', 'Europe/Warsaw')
  return url.toString()
})()

type CameraStatus = 'loading' | 'live' | 'stale'

type WeatherCurrent = {
  temperature_2m: number
  cloud_cover: number
  wind_speed_10m: number
  wind_direction_10m: number
  weather_code: number
  time: string
}

type WeatherResponse = {
  current: WeatherCurrent
  current_units?: Partial<Record<keyof WeatherCurrent, string>>
}

type TelescopePosition = {
  azimuthDegrees: number
  elevationDegrees: number
  targetName?: string
}

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: PAGE_GAP,
}

const cameraFrameStyle: CSSProperties = {
  background: '#f6f7f9',
  border: UI_BORDER,
  borderRadius: UI_BORDER_RADIUS_PX,
  overflow: 'hidden',
  textAlign: 'center',
}

const cameraImageStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  maxHeight: CAMERA_MAX_HEIGHT,
  objectFit: 'contain',
  background: '#111',
}

const weatherGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(${WEATHER_CARD_MIN_WIDTH}, 1fr))`,
  gap: CARD_GAP,
}

const telescopeGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns:
    `minmax(${TELESCOPE_LABEL_MIN_WIDTH}, ${TELESCOPE_LABEL_COLUMN_FRACTION}) `
    + `repeat(${TELESCOPE_VALUE_COLUMN_COUNT}, minmax(${TELESCOPE_VALUE_MIN_WIDTH}, 1fr))`,
  gap: CARD_GAP,
  alignItems: 'stretch',
}

const metricStyle: CSSProperties = {
  border: UI_BORDER,
  borderRadius: UI_BORDER_RADIUS_PX,
  padding: METRIC_PADDING,
  background: '#fff',
}

const metricLabelStyle: CSSProperties = {
  color: '#666',
  fontSize: METRIC_LABEL_FONT_SIZE,
  marginBottom: METRIC_LABEL_MARGIN_BOTTOM,
}

const metricValueStyle: CSSProperties = {
  fontSize: METRIC_VALUE_FONT_SIZE,
  fontWeight: METRIC_VALUE_FONT_WEIGHT,
}

const statusTextStyle: CSSProperties = {
  color: '#666',
  fontSize: METRIC_LABEL_FONT_SIZE,
  marginTop: STATUS_MARGIN_TOP,
}

const loadingCameraStyle: CSSProperties = {
  padding: LOADING_CAMERA_PADDING,
}

const weatherCodeLabels: Record<number, string> = {
  [WMO_WEATHER_CODES.CLEAR_SKY]: 'Clear sky',
  [WMO_WEATHER_CODES.MAINLY_CLEAR]: 'Mainly clear',
  [WMO_WEATHER_CODES.PARTLY_CLOUDY]: 'Partly cloudy',
  [WMO_WEATHER_CODES.OVERCAST]: 'Overcast',
  [WMO_WEATHER_CODES.FOG]: 'Fog',
  [WMO_WEATHER_CODES.DEPOSITING_RIME_FOG]: 'Depositing rime fog',
  [WMO_WEATHER_CODES.LIGHT_DRIZZLE]: 'Light drizzle',
  [WMO_WEATHER_CODES.MODERATE_DRIZZLE]: 'Moderate drizzle',
  [WMO_WEATHER_CODES.DENSE_DRIZZLE]: 'Dense drizzle',
  [WMO_WEATHER_CODES.SLIGHT_RAIN]: 'Slight rain',
  [WMO_WEATHER_CODES.MODERATE_RAIN]: 'Moderate rain',
  [WMO_WEATHER_CODES.HEAVY_RAIN]: 'Heavy rain',
  [WMO_WEATHER_CODES.SLIGHT_SNOW]: 'Slight snow',
  [WMO_WEATHER_CODES.MODERATE_SNOW]: 'Moderate snow',
  [WMO_WEATHER_CODES.HEAVY_SNOW]: 'Heavy snow',
  [WMO_WEATHER_CODES.SLIGHT_RAIN_SHOWERS]: 'Slight rain showers',
  [WMO_WEATHER_CODES.MODERATE_RAIN_SHOWERS]: 'Moderate rain showers',
  [WMO_WEATHER_CODES.VIOLENT_RAIN_SHOWERS]: 'Violent rain showers',
  [WMO_WEATHER_CODES.THUNDERSTORM]: 'Thunderstorm',
  [WMO_WEATHER_CODES.THUNDERSTORM_SLIGHT_HAIL]: 'Thunderstorm with slight hail',
  [WMO_WEATHER_CODES.THUNDERSTORM_HEAVY_HAIL]: 'Thunderstorm with heavy hail',
}

function formatWindDirection(degrees: number) {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
  return directions[Math.round(degrees / WIND_DIRECTION_SECTOR_DEGREES) % directions.length]
}

function metric(label: string, value: string) {
  return (
    <div style={metricStyle}>
      <div style={metricLabelStyle}>{label}</div>
      <div style={metricValueStyle}>{value}</div>
    </div>
  )
}

function emptyTelescopeCell() {
  return <div aria-hidden='true' />
}

function formatDegrees(value: Decimal.Value) {
  return `${new Decimal(value).toFixed(DEGREE_DECIMAL_PLACES)}°`
}

type HorizontalCoordinates = {
  azimuthDegrees: number
  elevationDegrees: number
}

type PointingDifference = {
  azimuthDegrees: number
  elevationDegrees: number
}

function toRadians(degrees: Decimal.Value) {
  return new Decimal(degrees).mul(Math.PI).div(HALF_CIRCLE_DEGREES).toNumber()
}

function toDegrees(radians: Decimal.Value) {
  return new Decimal(radians).mul(HALF_CIRCLE_DEGREES).div(Math.PI)
}

function normalizeDegrees(degrees: Decimal.Value) {
  return new Decimal(degrees).mod(FULL_CIRCLE_DEGREES).plus(FULL_CIRCLE_DEGREES).mod(FULL_CIRCLE_DEGREES)
}

function angularDiffDegrees(a: number, b: number) {
  return new Decimal(a)
    .minus(b)
    .plus(ANGULAR_DIFF_OFFSET_DEGREES)
    .mod(FULL_CIRCLE_DEGREES)
    .minus(HALF_CIRCLE_DEGREES)
    .toNumber()
}

function getJulianDate(date: Date) {
  return new Decimal(date.getTime()).div(MILLISECONDS_PER_DAY).plus(JULIAN_UNIX_EPOCH)
}

function calculateSunEquatorial(date: Date) {
  const daysSinceJ2000 = getJulianDate(date).minus(JULIAN_J2000_EPOCH)
  const meanLongitude = normalizeDegrees(
    SUN_MEAN_LONGITUDE_BASE_DEGREES.plus(SUN_MEAN_LONGITUDE_RATE_DEGREES.mul(daysSinceJ2000)),
  )
  const meanAnomaly = normalizeDegrees(
    SUN_MEAN_ANOMALY_BASE_DEGREES.plus(SUN_MEAN_ANOMALY_RATE_DEGREES.mul(daysSinceJ2000)),
  )
  const meanAnomalyRadians = toRadians(meanAnomaly)
  const eclipticLongitude = normalizeDegrees(
    meanLongitude
      .plus(SUN_ECLIPTIC_LONGITUDE_FIRST_COEFFICIENT.mul(Math.sin(meanAnomalyRadians)))
      .plus(SUN_ECLIPTIC_LONGITUDE_SECOND_COEFFICIENT.mul(
        Math.sin(SECOND_HARMONIC_MULTIPLIER * meanAnomalyRadians),
      )),
  )
  const obliquity = EARTH_OBLIQUITY_BASE_DEGREES.minus(EARTH_OBLIQUITY_RATE_DEGREES.mul(daysSinceJ2000))
  const eclipticLongitudeRadians = toRadians(eclipticLongitude)
  const obliquityRadians = toRadians(obliquity)
  const rightAscension = normalizeDegrees(
    toDegrees(
      Math.atan2(
        Math.cos(obliquityRadians) * Math.sin(eclipticLongitudeRadians),
        Math.cos(eclipticLongitudeRadians),
      ),
    ),
  )
  const declination = toDegrees(Math.asin(Math.sin(obliquityRadians) * Math.sin(eclipticLongitudeRadians)))

  return { rightAscensionDegrees: rightAscension, declinationDegrees: declination }
}

function calculateGreenwichMeanSiderealTime(date: Date) {
  const julianDate = getJulianDate(date)
  const daysSinceJ2000 = julianDate.minus(JULIAN_J2000_EPOCH)
  const centuriesSinceJ2000 = daysSinceJ2000.div(DAYS_PER_JULIAN_CENTURY)
  return normalizeDegrees(
    GREENWICH_MEAN_SIDEREAL_TIME_BASE_DEGREES
      .plus(GREENWICH_MEAN_SIDEREAL_TIME_RATE_DEGREES.mul(daysSinceJ2000))
      .plus(GREENWICH_MEAN_SIDEREAL_TIME_QUADRATIC_COEFFICIENT.mul(centuriesSinceJ2000.pow(SQUARE_EXPONENT)))
      .minus(centuriesSinceJ2000.pow(CUBE_EXPONENT).div(SIDEREAL_TIME_CUBIC_DIVISOR)),
  )
}

function equatorialToHorizontal(
  rightAscensionDegrees: Decimal.Value,
  declinationDegrees: Decimal.Value,
  date: Date,
): HorizontalCoordinates {
  const localSiderealTime = normalizeDegrees(
    calculateGreenwichMeanSiderealTime(date).plus(OBSERVATORY_LONGITUDE_DEGREES),
  )
  const hourAngleRadians = toRadians(normalizeDegrees(localSiderealTime.minus(rightAscensionDegrees)))
  const declinationRadians = toRadians(declinationDegrees)
  const latitudeRadians = toRadians(OBSERVATORY_LATITUDE_DEGREES)
  const elevationRadians = Math.asin(
    Math.sin(declinationRadians) * Math.sin(latitudeRadians)
      + Math.cos(declinationRadians) * Math.cos(latitudeRadians) * Math.cos(hourAngleRadians),
  )
  const azimuthRadians = Math.atan2(
    -Math.sin(hourAngleRadians),
    Math.tan(declinationRadians) * Math.cos(latitudeRadians)
      - Math.sin(latitudeRadians) * Math.cos(hourAngleRadians),
  )

  return {
    azimuthDegrees: normalizeDegrees(toDegrees(azimuthRadians)).toNumber(),
    elevationDegrees: toDegrees(elevationRadians).toNumber(),
  }
}

function calculateSunHorizontalCoordinates(date = new Date()) {
  const sun = calculateSunEquatorial(date)
  return equatorialToHorizontal(sun.rightAscensionDegrees, sun.declinationDegrees, date)
}

function isSunTarget(targetName?: string) {
  return targetName?.trim().toLowerCase() === 'sun'
}

function formatSignedDegrees(value: number) {
  const sign = value > POSITIVE_DEGREES_THRESHOLD ? '+' : ''
  return `${sign}${formatDegrees(value)}`
}

function parseTelescopePosition(body: string): TelescopePosition | undefined {
  try {
    const position = JSON.parse(body) as Partial<TelescopePosition>
    if (typeof position.azimuthDegrees !== 'number') return undefined
    if (typeof position.elevationDegrees !== 'number') return undefined
    return {
      azimuthDegrees: position.azimuthDegrees,
      elevationDegrees: position.elevationDegrees,
      targetName: typeof position.targetName === 'string' ? position.targetName : undefined,
    }
  } catch {
    return undefined
  }
}

function LiveView() {
  const [cameraSrc, setCameraSrc] = useState('')
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>('loading')
  const nextRequestIdRef = useRef(INITIAL_REQUEST_ID)
  const latestAcceptedIdRef = useRef(INITIAL_REQUEST_ID)
  const lastLoadedAtRef = useRef<number | null>(null)

  const [weather, setWeather] = useState<WeatherCurrent>()
  const [weatherUnits, setWeatherUnits] = useState<WeatherResponse['current_units']>()
  const [weatherError, setWeatherError] = useState(false)
  const [isWeatherLoading, setIsWeatherLoading] = useState(true)

  const [telescopePosition, setTelescopePosition] = useState<TelescopePosition>()
  const [isTelescopeLoading, setIsTelescopeLoading] = useState(true)
  const [telescopeError, setTelescopeError] = useState(false)
  const [sunCalculationTime, setSunCalculationTime] = useState(() => new Date())
  const telescopeSocketRef = useRef<{ disconnect?: () => void } | null>(null)

  useEffect(() => {
    let isMounted = true

    const refreshCamera = () => {
      nextRequestIdRef.current += 1
      const requestId = nextRequestIdRef.current
      const url = new URL(CAMERA_IMAGE_URL)
      url.searchParams.set('t', Date.now().toString())
      const nextUrl = url.toString()
      const image = new Image()

      image.onload = () => {
        if (!isMounted) return
        if (requestId <= latestAcceptedIdRef.current) return
        latestAcceptedIdRef.current = requestId
        lastLoadedAtRef.current = Date.now()
        setCameraSrc(nextUrl)
        setCameraStatus('live')
      }

      image.src = nextUrl
    }

    const checkStaleness = () => {
      if (!isMounted) return
      if (lastLoadedAtRef.current === null) return
      const age = Date.now() - lastLoadedAtRef.current
      setCameraStatus(age > CAMERA_STALE_MS ? 'stale' : 'live')
    }

    refreshCamera()
    const refreshId = window.setInterval(refreshCamera, CAMERA_REFRESH_MS)
    const stalenessId = window.setInterval(checkStaleness, CAMERA_STALENESS_CHECK_MS)

    return () => {
      isMounted = false
      window.clearInterval(refreshId)
      window.clearInterval(stalenessId)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    const fetchWeather = async () => {
      try {
        const response = await fetch(WEATHER_URL, { signal: controller.signal })

        if (!response.ok) {
          throw new Error(`Weather request failed with status ${response.status}`)
        }

        const data = (await response.json()) as WeatherResponse

        setWeather(data.current)
        setWeatherUnits(data.current_units)
        setWeatherError(false)
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        setWeatherError(true)
      } finally {
        if (!controller.signal.aborted) {
          setIsWeatherLoading(false)
        }
      }
    }

    fetchWeather()
    const intervalId = window.setInterval(fetchWeather, WEATHER_REFRESH_MS)

    return () => {
      controller.abort()
      window.clearInterval(intervalId)
    }
  }, [])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setSunCalculationTime(new Date())
    }, SUN_POSITION_REFRESH_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    const fetchTelescopePosition = async () => {
      try {
        const response = await fetch(TELESCOPE_POSITION_URL, { signal: controller.signal })

        if (response.status === HTTP_NOT_FOUND) {
          setTelescopePosition(undefined)
          setTelescopeError(false)
          return
        }

        if (!response.ok) {
          throw new Error(`Telescope position request failed with status ${response.status}`)
        }

        const position = (await response.json()) as TelescopePosition
        setTelescopePosition(position)
        setTelescopeError(false)
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        setTelescopeError(true)
      } finally {
        if (!controller.signal.aborted) {
          setIsTelescopeLoading(false)
        }
      }
    }

    const handleTelescopePosition = (message: { body: string }) => {
      const position = parseTelescopePosition(message.body)
      if (!position) return
      setTelescopePosition(position)
      setTelescopeError(false)
      setIsTelescopeLoading(false)
    }

    fetchTelescopePosition()
    telescopeSocketRef.current = reconnectingSocket(
      serverUrl + '/websocket',
      handleTelescopePosition,
      TELESCOPE_POSITION_TOPIC,
      window.location.href,
    )

    return () => {
      controller.abort()
      telescopeSocketRef.current?.disconnect?.()
      telescopeSocketRef.current = null
    }
  }, [])

  const weatherCondition = useMemo(() => {
    if (!weather) {
      return 'N/A'
    }

    return weatherCodeLabels[weather.weather_code] ?? `Code ${weather.weather_code}`
  }, [weather])

  const temperatureUnit = weatherUnits?.temperature_2m ?? '°C'
  const cloudCoverUnit = weatherUnits?.cloud_cover ?? '%'
  const windSpeedUnit = weatherUnits?.wind_speed_10m ?? 'km/h'
  const sunTargetPosition = useMemo(() => {
    if (!isSunTarget(telescopePosition?.targetName)) return undefined
    return calculateSunHorizontalCoordinates(sunCalculationTime)
  }, [sunCalculationTime, telescopePosition])
  const sunPointingDifference: PointingDifference | undefined = useMemo(() => {
    if (!telescopePosition || !sunTargetPosition) return undefined
    return {
      azimuthDegrees: angularDiffDegrees(telescopePosition.azimuthDegrees, sunTargetPosition.azimuthDegrees),
      elevationDegrees: new Decimal(telescopePosition.elevationDegrees)
        .minus(sunTargetPosition.elevationDegrees)
        .toNumber(),
    }
  }, [sunTargetPosition, telescopePosition])

  return (
    <div className='fade-in' style={pageStyle}>
      <Header as='h1' style={{ textAlign: 'center' }}>
        Receiver live view
      </Header>

      <section>
        <Header as='h2' style={{ textAlign: 'center' }}>
          Radio receiver camera
        </Header>
        <div style={cameraFrameStyle}>
          {cameraSrc ? (
            <img src={cameraSrc} alt='Radio receiver camera view' style={cameraImageStyle} />
          ) : (
            <div style={loadingCameraStyle}>Loading camera image...</div>
          )}
        </div>
        {cameraStatus === 'stale' && (
          <div style={statusTextStyle} role='status'>
            Camera feed appears offline (no new frame in the last {CAMERA_STALE_MS / MILLISECONDS_PER_SECOND}s).
          </div>
        )}
      </section>

      <Segment>
        <Header as='h2' style={{ textAlign: 'center' }}>
          Telescope position
        </Header>

        {isTelescopeLoading && !telescopePosition ? (
          <div>Loading telescope position...</div>
        ) : (
          <div style={telescopeGridStyle}>
            {metric('Target', telescopePosition?.targetName || 'N/A')}
            {metric(
              'Telescope azimuth',
              telescopePosition ? formatDegrees(telescopePosition.azimuthDegrees) : 'N/A',
            )}
            {metric(
              'Telescope elevation',
              telescopePosition ? formatDegrees(telescopePosition.elevationDegrees) : 'N/A',
            )}

            {sunTargetPosition && emptyTelescopeCell()}
            {sunTargetPosition && metric('Sun target azimuth', formatDegrees(sunTargetPosition.azimuthDegrees))}
            {sunTargetPosition && metric('Sun target elevation', formatDegrees(sunTargetPosition.elevationDegrees))}

            {sunPointingDifference && emptyTelescopeCell()}
            {sunPointingDifference
              && metric('Azimuth difference', formatSignedDegrees(sunPointingDifference.azimuthDegrees))}
            {sunPointingDifference
              && metric('Elevation difference', formatSignedDegrees(sunPointingDifference.elevationDegrees))}
          </div>
        )}

        {!isTelescopeLoading && !telescopePosition && !telescopeError && (
          <div style={statusTextStyle}>Waiting for telescope position...</div>
        )}
        {telescopeError && <div style={statusTextStyle}>Telescope position is currently unavailable.</div>}
      </Segment>

      <Segment>
        <Header as='h2' style={{ textAlign: 'center' }}>
          Weather in Białków
        </Header>

        {isWeatherLoading && !weather ? (
          <div>Loading weather...</div>
        ) : (
          <div style={weatherGridStyle}>
            {metric('Conditions', weatherCondition)}
            {metric('Temperature', weather ? `${weather.temperature_2m} ${temperatureUnit}` : 'N/A')}
            {metric('Cloud cover', weather ? `${weather.cloud_cover} ${cloudCoverUnit}` : 'N/A')}
            {metric(
              'Wind',
              weather
                ? `${weather.wind_speed_10m} ${windSpeedUnit} ${formatWindDirection(weather.wind_direction_10m)}`
                : 'N/A',
            )}
          </div>
        )}

        {weatherError && <div style={statusTextStyle}>Weather data is currently unavailable.</div>}
      </Segment>
    </div>
  )
}

export default LiveView
